'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/utils.js';

/*
  Edupra Drawer / Sheet
  ---------------------
  Side-anchored modal panel for data entry. Supports stacking — open
  another Drawer from inside an open one and it visually inset itself
  using the `stacked` prop, so the parent remains partially visible.

  Built on Radix Dialog for accessibility (focus trap, escape, aria).

  Usage:
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent size="md">
        <DrawerHeader eyebrow="Students" title="Register student" onClose={...} />
        <DrawerBody>...</DrawerBody>
        <DrawerFooter>...</DrawerFooter>
      </DrawerContent>
    </Drawer>
*/

const Drawer = DialogPrimitive.Root;
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerClose = DialogPrimitive.Close;

type DrawerSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<DrawerSize, string> = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[960px]',
};

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: DrawerSize;
  stacked?: boolean; // when true the drawer is offset to reveal its parent behind it
}

const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ className, children, size = 'md', stacked = false, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="ed-sheet-overlay" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn('ed-sheet', SIZE_CLASS[size], stacked && 'is-stacked', className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DrawerContent.displayName = 'DrawerContent';

interface DrawerHeaderProps {
  title: string;
  eyebrow?: string;
  description?: string;
  onClose?: () => void;
  actions?: React.ReactNode;
}

function DrawerHeader({ title, eyebrow, description, onClose, actions }: DrawerHeaderProps) {
  return (
    <header className="ed-sheet-header">
      <div className="min-w-0 flex-1">
        {eyebrow && <div className="ed-sheet-eyebrow">{eyebrow}</div>}
        <DialogPrimitive.Title asChild>
          <h2 className="ed-sheet-title truncate">{title}</h2>
        </DialogPrimitive.Title>
        {description && (
          <DialogPrimitive.Description asChild>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--fg-low)' }}>{description}</p>
          </DialogPrimitive.Description>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {actions}
        <DialogPrimitive.Close
          onClick={onClose}
          className="ed-btn-ghost ed-btn-sm ed-btn-icon"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2} />
        </DialogPrimitive.Close>
      </div>
    </header>
  );
}

function DrawerBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('ed-sheet-body', className)} {...props}>
      {children}
    </div>
  );
}

function DrawerFooter({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('ed-sheet-footer', className)} {...props}>
      {children}
    </div>
  );
}

interface DrawerFieldProps {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}

function DrawerField({ label, hint, required, error, children, className }: DrawerFieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label className="ed-label flex items-center gap-1.5">
        {label}
        {required && <span className="text-[var(--semantic-danger)]">*</span>}
      </label>
      {children}
      {error
        ? <p className="text-[11px] mt-0.5" style={{ color: 'var(--semantic-danger)' }}>{error}</p>
        : hint && <p className="text-[11px] mt-0.5" style={{ color: 'var(--fg-low)' }}>{hint}</p>
      }
    </div>
  );
}

interface DrawerSectionProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
}

function DrawerSection({ title, description, children }: DrawerSectionProps) {
  return (
    <section className="mb-6 last:mb-0">
      {title && (
        <div className="mb-3">
          <div className="ed-eyebrow">{title}</div>
          {description && (
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--fg-low)' }}>{description}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">{children}</div>
    </section>
  );
}

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerField,
  DrawerSection,
};
